import {
  ActivityExpressionUsage,
  ActivityExpressionValidationContext,
  ExpressionReturnType,
  expressionScopeRequestEvent,
  ExpressionScopeRequestPayload,
  isExpressionReturnType,
  parseAndValidateActivityExpression,
  printExpression
} from '@cumu/shared';
import { CoreExpression } from 'jsep';
import { HTMLValueElement } from '../forms/utilities';
import { addCustomValidator, setValidationMessage } from '../forms/validation';
import { dispatchChange } from './util';

/**
 * An input element that validates and formats expressions.
 * - Dispatches expression-scope-request event when validating/formatting.
 * - Circular reference validation is out of scope and should be handled by the ancestor.
 * - Column remapping is out of scope and should be handled by the ancestor.
 */
export class ExpressionInput extends HTMLElement {
  private timeout = 0;

  connectedCallback() {
    this.addEventListener('change', this);
    this.addEventListener('blur', this);
    this.addEventListener('keydown', this);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this);
    this.removeEventListener('blur', this);
    this.removeEventListener('keydown', this);
  }

  get input() {
    return this.querySelector('input')!;
  }

  get allowedTypes(): ExpressionReturnType[] {
    return (
      this.getAttribute('allowed-types')
        ?.split(/\s+/g)
        .filter(isExpressionReturnType) ?? []
    );
  }

  get usage(): ActivityExpressionUsage {
    return this.getAttribute('usage') as ActivityExpressionUsage;
  }

  handleEvent(event: Event) {
    if (event.target === this.input) {
      if (event instanceof KeyboardEvent) {
        if (event.key === 'Enter') {
          event.preventDefault();
        } else {
          return;
        }
      }
      if (event.isTrusted) {
        clearTimeout(this.timeout);
        this.processChange();
      }
    } else {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(this.processChange, 300);
    }
  }

  processChange = () => {
    if (this.input.value.trim() === '') {
      return;
    }

    const result = parseAndValidate(this.input, this.allowedTypes, this.usage);
    if (!result.valid) {
      setTimeout(() => {
        setValidationMessage(this.input, result.reason);
        this.closest('.form-group')?.classList.add('errored');
      });
      return;
    }

    const formatted = printExpression(result.originalNode);
    if (this.input.value !== formatted) {
      this.input.value = formatted;
      dispatchChange(this.input);
    }
  };
}

declare global {
  interface Window {
    ExpressionInput: typeof ExpressionInput;
  }
  interface HTMLElementTagNameMap {
    'expression-input': ExpressionInput;
  }
}

if (!window.customElements.get('expression-input')) {
  window.ExpressionInput = ExpressionInput;
  window.customElements.define('expression-input', ExpressionInput);
}

function parseAndValidate(
  input: HTMLInputElement,
  allowedTypes: ExpressionReturnType[],
  usage: ActivityExpressionUsage
):
  | {
      valid: true;
      type: ExpressionReturnType;
      node: CoreExpression;
      originalNode: CoreExpression;
    }
  | { valid: false; reason: string } {
  const event = new CustomEvent<ExpressionScopeRequestPayload>(
    expressionScopeRequestEvent,
    { bubbles: true, cancelable: true, detail: { identifiers: {} } }
  );
  if (input.dispatchEvent(event)) {
    return {
      valid: false,
      reason: 'Expression scope request was not handled.'
    };
  }
  const context: ActivityExpressionValidationContext = {
    properties: {},
    references: [],
    scanReferences: false,
    identifiers: event.detail.identifiers,
    usage
  };
  const result = parseAndValidateActivityExpression({
    expression: input.value,
    getContext: () => context
  });
  if (!result.valid) {
    return { valid: false, reason: result.reason };
  }
  if (allowedTypes.includes(result.type)) {
    return result;
  }
  return {
    valid: false,
    reason: `Expression must result in a ${allowedTypes.join(' / ')}.`
  };
}

addCustomValidator((input: HTMLValueElement) => {
  let exp: ExpressionInput | null;
  if (
    input instanceof HTMLInputElement &&
    input.type === 'text' &&
    input.value.trim() !== '' &&
    (exp = input.closest('expression-input'))
  ) {
    const result = parseAndValidate(input, exp.allowedTypes, exp.usage);
    if (!result.valid) {
      return result.reason;
    }
  }
  return null;
});
